/**
 * WellDoneScreen.tsx - Displays a congratulatory screen after completing an experiment
 * Last updated: 2025-08-12 15:27:21
 * Updated by: JulianPolly
 */

import React, { useEffect, useRef } from "react";
import { NonIdealState, Icon, Button } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import confetti from "canvas-confetti";
import { useNavigate, useParams } from "react-router";
import { useSession } from "./SessionContext";
import { useLanguage } from "../context/LanguageContext"; // Import the language hook

// Define praise phrases for each language
const PRAISES_DE = [
    "Super gemacht!",
    "Du bist spitze!",
    "Großartige Leistung!",
    "Sehr gut gelöst!",
    "Klasse, weiter so!",
    "Das war beeindruckend!",
    "Bravo!",
    "Toll abgeschlossen!",
    "Starke Arbeit!",
    "Respekt!",
    "Gut durchgezogen!",
    "Das hat richtig überzeugt!",
    "Du hast das toll gemeistert!",
    "Volle Punktzahl in Einsatz!",
    "Mit so einem Einsatz macht's Spaß!",
    "Da steckt echte Konzentration drin!",
    "Eine Runde Sache – weiter so!",
    "Sehr fokussiert gearbeitet!",
    "Du hast das Beste rausgeholt!",
    "Richtig stark geblieben bis zum Schluss!"
];

const PRAISES_EN = [
    "Well done!",
    "You're awesome!",
    "Great job!",
    "Excellent work!",
    "Fantastic effort!",
    "That was impressive!",
    "Bravo!",
    "Nicely completed!",
    "Strong work!",
    "Respect!",
    "Way to go!",
    "That was really convincing!",
    "You've mastered this!",
    "Full marks for effort!",
    "With such commitment, it's a pleasure!",
    "That shows real concentration!",
    "A job well done - keep it up!",
    "Very focused work!",
    "You got the most out of it!",
    "Stayed strong right to the end!"
];

function getRandomPraise(language: string) {
    const praises = language === 'en' ? PRAISES_EN : PRAISES_DE;
    return praises[Math.floor(Math.random() * praises.length)];
}

export const WellDoneScreen = () => {
    const navigate = useNavigate();
    const { sequenceId } = useParams();
    const { userData, writeUserData } = useSession();
    const { language } = useLanguage(); // Get current language

    // Ref for the congratulatory element
    const congratsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (congratsRef.current) {
            // Position of the element relative to viewport
            const rect = congratsRef.current.getBoundingClientRect();
            // y-center of the element relative to the window
            const y = (rect.top + rect.height / 2) / window.innerHeight;
            confetti({
                particleCount: 200,
                spread: 120,
                origin: { y }
            });
        } else {
            // Fallback: as before
            confetti({
                particleCount: 200,
                spread: 120,
                origin: { y: 0.6 }
            });
        }
    }, []);

    const praise = getRandomPraise(language);

    const handleBackToHome = () => {
        if (sequenceId) {
            writeUserData({
                ...userData,
                finishedExperiments: {
                    ...(userData.finishedExperiments || {}),
                    [sequenceId]: true
                }
            });
        }
        navigate("/app/");
    };

    const handleShowResults = () => {
        if (sequenceId) {
            navigate(`/app/sequences/${sequenceId}/${FIRST_RESULT_SCREEN_INDEX}`);
        }
    };

    // Translated texts
    const wellDoneText = language === 'en' ? 'Well done!' : 'Sehr gut!';
    const moduleCompletedText = language === 'en'
        ? 'You have successfully completed this module.'
        : 'Du hast das Modul erfolgreich abgeschlossen.';
    const keepItUpText = language === 'en' ? 'Keep it up!' : 'Weiter so!';
    const backToOverviewText = language === 'en' ? 'Back to Overview' : 'Zurück zur Übersicht';
    const viewResultsText = language === 'en' ? 'View Results' : 'Ergebnis ansehen';

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 8 }}>
            <div style={{ fontSize: "2.8em", fontWeight: 600, marginBottom: 8 }}>{wellDoneText}</div>
            <Icon icon="endorsed" iconSize={128} style={{ color: "#FFC940", marginBottom: 12 }} />
            <div style={{ fontSize: "1.4em", marginBottom: 8, fontWeight: 500 }}>{praise}</div>
            <NonIdealState
                icon={undefined}
                title={undefined}
                description={
                    <div style={{ fontSize: "1.2em" }}>
                        {moduleCompletedText}<br />
                        <span ref={congratsRef}>{keepItUpText}</span>
                    </div>
                }
            />
            <div style={{ display: "flex", gap: 16, marginTop: 32 }}>
                <Button
                    icon={IconNames.HOME}
                    large
                    intent="primary"
                    onClick={handleBackToHome}
                >
                    {backToOverviewText}
                </Button>
                <Button
                    icon={IconNames.CHART}
                    large
                    intent="success"
                    onClick={handleShowResults}
                >
                    {viewResultsText}
                </Button>
            </div>
        </div>
    );
};

const FIRST_RESULT_SCREEN_INDEX = 1;
